import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Autorenew as RenewIcon,
  Description as FileIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';

const TenantSubscriptionsManagement = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // History dialog state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantHistory, setTenantHistory] = useState([]);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await apiService.get(`/superadmin/tenant-subscriptions?${params.toString()}`);
      if (response.success && response.data?.subscriptions) {
        // Store all subscriptions for history lookup
        setAllSubscriptions(response.data.subscriptions);
        
        // Group by tenant and get latest subscription for each
        const latestByTenant = {};
        response.data.subscriptions.forEach(sub => {
          const tenantId = sub.tenant_id;
          if (!latestByTenant[tenantId] || new Date(sub.created_at) > new Date(latestByTenant[tenantId].created_at)) {
            latestByTenant[tenantId] = sub;
          }
        });
        setSubscriptions(Object.values(latestByTenant));
      } else {
        setSubscriptions([]);
        setAllSubscriptions([]);
      }
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'trial': return 'info';
      case 'expired': return 'error';
      case 'cancelled': return 'default';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMoney = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  /**
   * Amount actually paid = after discount, including tax.
   * Backend renew flow stores this as tenant_subscriptions.total_amount (from input.final_amount).
   */
  const getAmountPaid = (sub) => {
    const total = sub?.total_amount ?? sub?.final_amount ?? sub?.amount_paid;
    const totalNum = Number(total);
    if (Number.isFinite(totalNum) && totalNum > 0) return totalNum;

    // Fallback if total isn't present but gross + tax are.
    const gross = Number(sub?.gross_total ?? sub?.payment_amount);
    const tax = Number(sub?.tax_amount);
    if (Number.isFinite(gross) && Number.isFinite(tax) && gross + tax > 0) return gross + tax;

    // Last resort: show plan price (not "paid", but better than 0 when data isn't populated).
    const plan = Number(sub?.price ?? sub?.plan_price);
    if (Number.isFinite(plan) && plan > 0) return plan;

    return 0;
  };

  const handleSearch = () => {
    fetchSubscriptions();
  };

  const handleViewHistory = (subscription) => {
    setSelectedTenant(subscription);
    // Get all subscriptions for this tenant, sorted by created_at descending
    const history = allSubscriptions
      .filter(sub => sub.tenant_id === subscription.tenant_id)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setTenantHistory(history);
    setHistoryOpen(true);
  };

  const handleCloseHistory = () => {
    setHistoryOpen(false);
    setSelectedTenant(null);
    setTenantHistory([]);
  };

  const handleRenew = (subscription) => {
    navigate('/superadmin/tenant-subscriptions/renew', {
      state: { subscription }
    });
  };

  const handleViewInvoice = (subscription) => {
    navigate(`/superadmin/tenant-subscriptions/invoice/${subscription.id}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Tenant Subscriptions
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by tenant name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchSubscriptions} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell><strong>Tenant</strong></TableCell>
                <TableCell><strong>Plan</strong></TableCell>
                <TableCell><strong>Start Date</strong></TableCell>
                <TableCell><strong>End Date</strong></TableCell>
                <TableCell><strong>Amount Paid</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No subscriptions found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                subscriptions.map((sub) => (
                  <TableRow key={sub.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{sub.tenant_name || `Tenant #${sub.tenant_id}`}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sub.tenant_email}
                      </Typography>
                    </TableCell>
                    <TableCell>{sub.plan_name || '-'}</TableCell>
                    <TableCell>{formatDate(sub.start_date)}</TableCell>
                    <TableCell>{formatDate(sub.end_date)}</TableCell>
                    <TableCell>₹{formatMoney(getAmountPaid(sub))}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View Subscription History">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewHistory(sub)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Renew Subscription">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleRenew(sub)}
                          >
                            <RenewIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Subscription History Dialog */}
      <Dialog 
        open={historyOpen} 
        onClose={handleCloseHistory}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">
              Subscription History - {selectedTenant?.tenant_name || 'Tenant'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseHistory} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {tenantHistory.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No subscription history found
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Plan</strong></TableCell>
                    <TableCell><strong>Start Date</strong></TableCell>
                    <TableCell><strong>End Date</strong></TableCell>
                    <TableCell><strong>Amount Paid</strong></TableCell>
                    <TableCell><strong>Created At</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenantHistory.map((sub, index) => (
                    <TableRow 
                      key={sub.id} 
                      hover
                      sx={index === 0 ? { backgroundColor: 'primary.50' } : {}}
                    >
                      <TableCell>
                        {index === 0 ? (
                          <Chip label="Latest" size="small" color="primary" variant="outlined" />
                        ) : (
                          index + 1
                        )}
                      </TableCell>
                      <TableCell>{sub.plan_name || '-'}</TableCell>
                      <TableCell>{formatDate(sub.start_date)}</TableCell>
                      <TableCell>{formatDate(sub.end_date)}</TableCell>
                      <TableCell>₹{formatMoney(getAmountPaid(sub))}</TableCell>
                      <TableCell>{formatDateTime(sub.created_at)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Invoice">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewInvoice(sub)}
                          >
                            <FileIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistory}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantSubscriptionsManagement;
